import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Company as CompanyType } from "@/types/invoice";
import { Building2, MapPin, CreditCard, Receipt, Landmark, Check, X, IdCard } from "lucide-react";

export default function Company() {
  const [company, setCompany] = useState<CompanyType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameValue, setNameValue] = useState("");

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        setIsLoading(true);
        const { data: authData } = await supabase.auth.getUser();
        if (!authData.user) return;

        const { data: profileData, error: profileError } = await supabase
          .from('users')
          .select('company_id')
          .eq('user_id', authData.user.id)
          .single();

        if (profileError) throw profileError;
        if (!profileData?.company_id) return;

        const { data: companyData, error: companyError } = await supabase
          .from('companies')
          .select('*')
          .eq('id', profileData.company_id)
          .single();

        if (companyError) throw companyError;
        setCompany(companyData as CompanyType);
        
        if (companyData.logo_url) {
          setLogoPreview(companyData.logo_url);
        }
      } catch (error) {
        console.error("Error fetching company:", error);
        toast.error("Failed to load company information");
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompany();
  }, []);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const uploadLogo = async () => {
    if (!logoFile || !company) return null;

    try {
      const fileExt = logoFile.name.split('.').pop();
      const fileName = `${company.id}-logo-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { data: buckets } = await supabase.storage.listBuckets();
      if (!buckets?.find(bucket => bucket.name === 'company-logos')) {
        await supabase.storage.createBucket('company-logos', {
          public: true
        });
      }

      const { error: uploadError, data } = await supabase.storage
        .from('company-logos')
        .upload(filePath, logoFile);

      if (uploadError) {
        toast.error("Failed to upload logo: " + uploadError.message);
        return null;
      }

      const { data: urlData } = supabase.storage.from('company-logos').getPublicUrl(filePath);
      
      const { error: updateError } = await supabase
        .from('companies')
        .update({ logo_url: urlData.publicUrl })
        .eq('id', company.id);
        
      if (updateError) {
        toast.error("Failed to update company with new logo");
        return null;
      }
      
      return urlData.publicUrl;
    } catch (error) {
      console.error("Error uploading logo:", error);
      return null;
    }
  };

  const updateCompanyName = async (newName: string) => {
    if (!company) return;

    try {
      setIsUpdating(true);
      
      const { error } = await supabase
        .from('companies')
        .update({ name: newName })
        .eq('id', company.id);

      if (error) throw error;

      setCompany({
        ...company,
        name: newName
      });

      toast.success("Company name updated successfully");
    } catch (error) {
      console.error("Error updating company name:", error);
      toast.error("Failed to update company name");
    } finally {
      setIsUpdating(false);
      setIsEditingName(false);
    }
  };

  const updateField = async (field: string, value: string) => {
    if (!company) return false;

    try {
      console.log(`Updating field: ${field} with value: ${value}`);
      
      const { error } = await supabase
        .from('companies')
        .update({ [field]: value })
        .eq('id', company.id);

      if (error) {
        console.error(`Error updating ${field}:`, error);
        toast.error(`Failed to update ${field}`);
        return false;
      }

      setCompany({
        ...company,
        [field]: value
      });

      toast.success(`${field} updated successfully`);
      return true;
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      toast.error(`Failed to update ${field}`);
      return false;
    }
  };

  const updateBankDetail = async (field: string, value: string) => {
    if (!company) return false;

    const currentDetails = company.bank_details as Record<string, any> || {};
    const newDetails = { ...currentDetails, [field]: value };

    try {
      const { error } = await supabase
        .from('companies')
        .update({ bank_details: newDetails })
        .eq('id', company.id);

      if (error) {
        toast.error(`Failed to update ${field}`);
        return false;
      }

      setCompany({
        ...company,
        bank_details: newDetails
      });

      toast.success(`${field} updated successfully`);
      return true;
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      toast.error(`Failed to update ${field}`);
      return false;
    }
  };

  const EditableField = ({ 
    icon: Icon, 
    label, 
    value, 
    onSave,
    textarea = false
  }: { 
    icon: React.ElementType; 
    label: string; 
    value?: string | null;
    onSave: (value: string) => Promise<boolean>;
    textarea?: boolean;
  }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value || '');

    const handleSave = async () => {
      const success = await onSave(editValue);
      if (success) {
        setIsEditing(false);
      }
    };

    return (
      <div className="flex items-start gap-2">
        <Icon className="h-5 w-5 mt-0.5 text-muted-foreground" />
        <div className="flex-1">
          <p className="font-medium">{label}</p>
          {isEditing ? (
            <div className="flex flex-col gap-2 mt-1">
              {textarea ? (
                <Textarea 
                  value={editValue} 
                  onChange={(e) => setEditValue(e.target.value)}
                  className="min-h-24"
                />
              ) : (
                <Input 
                  value={editValue} 
                  onChange={(e) => setEditValue(e.target.value)}
                  className="h-8"
                />
              )}
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSave}>Save</Button>
                <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
              </div>
            </div>
          ) : (
            <div 
              className="cursor-pointer hover:bg-accent hover:text-accent-foreground p-1 rounded-md -ml-1"
              onClick={() => setIsEditing(true)}
            >
              {value ? (
                <p className="text-muted-foreground">{value}</p>
              ) : (
                <div className="flex items-center gap-1 text-muted-foreground">
                  <span className="text-sm">Click to add {label.toLowerCase()}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <h1 className="text-2xl font-bold mb-4">Company Settings</h1>
        <p>Loading company information...</p>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="p-8">
        <h1 className="text-2xl font-bold mb-4">Company Settings</h1>
        <p>No company information found.</p>
      </div>
    );
  }

  const bankDetails = company.bank_details as {
    account_number?: string;
    recipient_name?: string;
    bank_name?: string;
    swift_code?: string;
    location?: string;
    tax_id?: string;
  } | null || {};

  return (
    <div className="p-8 space-y-6">
      {isEditingName ? (
        <div className="flex items-center gap-2">
          <Input
            value={nameValue}
            onChange={(e) => setNameValue(e.target.value)}
            className="text-3xl font-bold h-12"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") updateCompanyName(nameValue);
              if (e.key === "Escape") setIsEditingName(false);
            }}
          />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => updateCompanyName(nameValue)}
          >
            <Check className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsEditingName(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <h1
          className="text-3xl font-bold cursor-pointer hover:bg-accent hover:text-accent-foreground p-2 rounded-md -ml-2"
          onClick={() => {
            setNameValue(company.name);
            setIsEditingName(true);
          }}
        >
          {company.name}
        </h1>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Company Information
            </CardTitle>
            <CardDescription>
              Update your company's basic information
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <EditableField 
              icon={IdCard} 
              label="Legal Name" 
              value={company.legal_name} 
              onSave={(value) => updateField('legal_name', value)}
            />
            <EditableField 
              icon={MapPin} 
              label="Address" 
              value={company.address} 
              onSave={(value) => updateField('address', value)}
              textarea
            />
            <EditableField 
              icon={Receipt} 
              label="Tax ID" 
              value={bankDetails?.tax_id} 
              onSave={(value) => updateBankDetail('tax_id', value)}
            />
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Company Logo</CardTitle>
            <CardDescription>
              Upload your company logo for invoices and documents
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col items-center space-y-4">
              {logoPreview ? (
                <div className="relative">
                  <img 
                    src={logoPreview} 
                    alt="Company Logo Preview" 
                    className="max-h-32 max-w-full border rounded"
                  />
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    className="absolute -top-2 -right-2 rounded-full h-6 w-6 p-0"
                    onClick={async () => {
                      setLogoPreview(null);
                      setLogoFile(null);
                      
                      if (company) {
                        const { error } = await supabase
                          .from('companies')
                          .update({ logo_url: null })
                          .eq('id', company.id);
                          
                        if (error) {
                          toast.error("Failed to remove logo");
                          return;
                        }
                        
                        setCompany({...company, logo_url: null});
                        toast.success("Logo removed successfully");
                      }
                    }}
                  >
                    X
                  </Button>
                </div>
              ) : (
                <div className="h-32 w-48 bg-gray-100 flex items-center justify-center rounded border">
                  <span className="text-gray-400">No logo uploaded</span>
                </div>
              )}
              
              <div className="space-y-2 w-full">
                <Label htmlFor="logo">Upload Logo</Label>
                <Input 
                  id="logo" 
                  type="file" 
                  accept="image/*"
                  onChange={handleLogoChange}
                />
                {logoFile && (
                  <Button 
                    className="w-full mt-2"
                    onClick={async () => {
                      const logoUrl = await uploadLogo();
                      if (logoUrl && company) {
                        setCompany({...company, logo_url: logoUrl});
                        toast.success("Logo uploaded successfully");
                      }
                    }}
                  >
                    Upload Logo
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Landmark className="h-5 w-5" />
            Banking Information
          </CardTitle>
          <CardDescription>
            Update your company's banking details for invoices
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <EditableField 
              icon={Landmark} 
              label="Bank Name" 
              value={bankDetails?.bank_name} 
              onSave={(value) => updateBankDetail('bank_name', value)}
            />
            
            <EditableField 
              icon={CreditCard} 
              label="Account Number" 
              value={bankDetails?.account_number} 
              onSave={(value) => updateBankDetail('account_number', value)}
            />
            
            <EditableField 
              icon={Building2} 
              label="Recipient Name" 
              value={bankDetails?.recipient_name} 
              onSave={(value) => updateBankDetail('recipient_name', value)}
            />
            
            <EditableField 
              icon={CreditCard} 
              label="Swift Code" 
              value={bankDetails?.swift_code} 
              onSave={(value) => updateBankDetail('swift_code', value)}
            />
            
            <EditableField 
              icon={MapPin} 
              label="Bank Location" 
              value={bankDetails?.location} 
              onSave={(value) => updateBankDetail('location', value)}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
